import React, { useEffect, useState } from 'react';
import { Box, Stack, Switch, Typography } from '@mui/material';
import { collection, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { UI } from './settingsUi';
import { JOURNEY_AUTOMATIONS } from '../../utils/whatsappJourney';
import type { JourneyAutomation } from '../../utils/whatsappJourney';
import { isFlowEnabled } from '../../utils/whatsappWorkingHours';
import type { WhatsappAutoReplySettings } from '../../utils/whatsappWorkingHours';

/** Pseudo trigger for the old birthday sender (birthdayWishConfig/main). */
export const OLD_BIRTHDAY_TRIGGER = 'old-birthday';

/** One piece of the older automation: a rule, or the old birthday sender. */
export interface OldItem {
  kind: 'rule' | 'birthday';
  id: string;
  name: string;
  trigger: string;
  recipient: string;
  active: boolean;
  /** Switched off from the WhatsApp hub (so it can be offered back). */
  disabledByHub: boolean;
}

/** Live view of the older automation, with on/off actions. */
export const useOldAutomation = () => {
  const { currentUser } = useAuth();
  const [rules, setRules] = useState<OldItem[]>([]);
  const [birthday, setBirthday] = useState<OldItem | null>(null);

  useEffect(
    () =>
      onSnapshot(
        collection(db, 'whatsappAutomationRules'),
        (snap) =>
          setRules(
            snap.docs.map((d) => {
              const r = d.data() as any;
              return {
                kind: 'rule' as const,
                id: d.id,
                name: String(r.name || 'Unnamed rule'),
                trigger: String(r.trigger || ''),
                recipient: String(r.recipient || 'customer'),
                active: r.active !== false,
                disabledByHub: r.disabledByHub === true,
              };
            })
          ),
        () => setRules([])
      ),
    []
  );

  useEffect(
    () =>
      onSnapshot(
        doc(db, 'birthdayWishConfig', 'main'),
        (snap) => {
          if (!snap.exists()) {
            setBirthday(null);
            return;
          }
          const data = snap.data() as any;
          setBirthday({
            kind: 'birthday',
            id: 'main',
            name: 'Birthday wishes (older sender)',
            trigger: OLD_BIRTHDAY_TRIGGER,
            recipient: 'customers, brokers, staff',
            active: data.enabled === true,
            disabledByHub: data.disabledByHub === true,
          });
        },
        () => setBirthday(null)
      ),
    []
  );

  const items = birthday ? [...rules, birthday] : rules;

  const setActive = async (item: OldItem, active: boolean) => {
    const meta = {
      disabledByHub: !active,
      [active ? 'enabledByHubAt' : 'disabledByHubAt']: serverTimestamp(),
      hubChangedByEmail: currentUser?.email || null,
    };
    if (item.kind === 'birthday') {
      await setDoc(doc(db, 'birthdayWishConfig', 'main'), { enabled: active, ...meta }, { merge: true });
    } else {
      await setDoc(doc(db, 'whatsappAutomationRules', item.id), { active, ...meta }, { merge: true });
    }
  };

  /** Older items that send the same thing as this journey automation. */
  const overlapsFor = (automation: JourneyAutomation) =>
    items.filter((i) => automation.oldRuleTriggers?.includes(i.trigger) &&
      (automation.key !== 'visitBrochure' || i.recipient === 'customer'));

  /** Active older items whose new replacement is switched on: candidates at go-live. */
  const goLiveCandidates = (settings: WhatsappAutoReplySettings) =>
    items
      .filter((i) => i.active)
      .map((i) => ({
        item: i,
        replacedBy: JOURNEY_AUTOMATIONS.filter((a) => a.oldRuleTriggers?.includes(i.trigger) &&
          (a.key !== 'visitBrochure' || i.recipient === 'customer') && isFlowEnabled(settings, a.key)),
      }))
      .filter((row) => row.replacedBy.length > 0);

  return { items, setActive, overlapsFor, goLiveCandidates };
};

export type OldAutomationApi = ReturnType<typeof useOldAutomation>;

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#8a5a12' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#d08a1e' },
};

/** One older rule with its own on/off switch. */
export const OldItemRow = ({ item, api, note }: { item: OldItem; api: OldAutomationApi; note?: React.ReactNode }) => {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await api.setActive(item, !item.active);
    } finally {
      setBusy(false);
    }
  };
  const toBroker = /broker/i.test(item.recipient);
  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ py: 0.4 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, color: UI.ink }}>
          {item.name}
          {item.disabledByHub && !item.active && (
            <Box component="span" sx={{ color: UI.muted }}> · turned off from the hub</Box>
          )}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: toBroker ? '#8a5a12' : UI.muted }}>
          {item.kind === 'birthday' ? 'Older birthday sender' : `Trigger: ${item.trigger || 'unknown'}`} · sends to {item.recipient}
          {toBroker ? ' (keep it on if brokers still need it)' : ''}
          {note}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 11.5, color: item.active ? '#8a5a12' : UI.muted }}>{item.active ? 'On' : 'Off'}</Typography>
      <Switch size="small" checked={item.active} disabled={busy} onChange={toggle} inputProps={{ 'aria-label': `${item.name} on or off` }} sx={switchSx} />
    </Stack>
  );
};
